import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Clock, User, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSessoesEspera = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessoes, setSessoes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessoes();
    // Refresh every 10 seconds
    const interval = setInterval(fetchSessoes, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchSessoes = async () => {
    try {
      const response = await axios.get(`${API}/admin/sessoes/espera`);
      setSessoes(response.data);
    } catch (error) {
      toast.error('Erro ao carregar sessões');
    } finally {
      setLoading(false);
    }
  };

  const handleEntrarEstacao = async (sessaoId) => {
    try {
      await axios.put(`${API}/admin/sessoes/${sessaoId}/entrar`);
      toast.success('Entrando na sessão...');
      navigate(`/admin/sessao/${sessaoId}`);
    } catch (error) {
      toast.error('Erro ao entrar na sessão');
    }
  };

  const handleVoltar = () => {
    navigate('/admin');
  };

  const getTempoEspera = (criadoEm) => {
    const agora = new Date();
    const criado = new Date(criadoEm);
    const diffMs = agora - criado;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Agora mesmo';
    if (diffMins === 1) return '1 minuto';
    return `${diffMins} minutos`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#191919]">
        <div className="text-white text-xl">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#191919] text-white">
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <Button
            onClick={handleVoltar}
            variant="ghost"
            className="text-gray-400 hover:text-white mb-2"
            data-testid="voltar-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Estações em Espera</h1>
            <p className="text-gray-400 text-sm mt-1">
              {sessoes.length} {sessoes.length === 1 ? 'sessão aguardando' : 'sessões aguardando'}
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {sessoes.length === 0 ? (
          <Card className="bg-[#0f0f0f] border-gray-800">
            <CardContent className="pt-12 pb-12 text-center">
              <Clock className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">Nenhuma sessão aguardando</h3>
              <p className="text-gray-400">Quando usuários iniciarem sessões, elas aparecerão aqui.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sessoes.map((sessao) => (
              <Card
                key={sessao.id}
                className="bg-white border-gray-200 hover:shadow-lg transition-shadow"
                data-testid={`sessao-card-${sessao.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-[#191919] text-xl mb-2">
                        {sessao.estacaoNome}
                      </CardTitle>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <MapPin className="w-4 h-4" />
                          <span className="font-semibold">{sessao.especialidadeNome}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <User className="w-4 h-4" />
                          <span>Médico: <strong>{sessao.usuarioNome}</strong></span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>Esperando há: <strong>{getTempoEspera(sessao.criadoEm)}</strong></span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <div className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-500/20 rounded-full">
                        <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                        <span className="text-yellow-600 font-semibold text-xs uppercase">Aguardando</span>
                      </div>
                      <Button
                        onClick={() => handleEntrarEstacao(sessao.id)}
                        className="bg-[#CF0D1D] hover:bg-[#A00A17] text-white"
                        data-testid={`entrar-button-${sessao.id}`}
                      >
                        Entrar na Estação
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      <span>Tempo máximo: </span>
                      <strong>{sessao.tempoMaximoMinutos} minutos</strong>
                    </div>
                    <div className="text-sm text-gray-500">
                      Criada às {new Date(sessao.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSessoesEspera;